<template>
  <transition name="zm-zoom-in-top" @after-enter="handleEnter" @after-leave="handleLeave">
    <div
      v-show="visible"
      class="zm-picker-panel zm-date-range-picker zm-popper"
      role="dialog"
      aria-label="Date Range Picker Calendar view"
      :class="[{
        'has-sidebar': $slots.sidebar || shortcuts,
        'has-time': showTime
      }, popperClass]">
      <div class="zm-picker-panel__body-wrapper" role="application">
        <slot name="sidebar" class="zm-picker-panel__sidebar"></slot>
        <div class="zm-picker-panel__sidebar" v-if="shortcuts">
          <button
            type="button"
            class="zm-picker-panel__shortcut"
            v-for="(shortcut, key) in shortcuts"
            :key="key"
            @click="handleShortcutClick(shortcut)">{{shortcut.text}}
          </button>
        </div>
        <div class="zm-picker-panel__body">
          <div class="zm-date-range-picker__time-header" v-if="showTime">
            <span class="zm-date-range-picker__editors-wrap">
              <span class="zm-date-range-picker__time-picker-wrap">
                <zm-input
                  size="small"
                  :disabled="rangeState.selecting"
                  ref="minInput"
                  :placeholder="t('datepicker.startDate')"
                  class="zm-date-range-picker__editor"
                  :value="minVisibleDate"
                  @input.native="handleDateInput($event, 'min')"
                  @change.native="handleDateChange($event, 'min')"/>
              </span>
              <span class="zm-date-range-picker__time-picker-wrap" v-clickoutside="handleMinTimeClose">
                <zm-input
                  size="small"
                  :disabled="rangeState.selecting"
                  :placeholder="t('datepicker.startTime')"
                  class="zm-date-range-picker__editor"
                  :value="minVisibleTime"
                  @focus="minTimePickerVisible = true"
                  @change.native="handleTimeChange($event, 'min')"/>
                <time-picker
                  ref="minTimePicker"
                  @pick="handleMinTimePick"
                  :time-arrow-control="arrowControl"
                  :visible="minTimePickerVisible"
                  @mounted="$refs.minTimePicker.format=timeFormat">
                </time-picker>
              </span>
            </span>
            <span class="zm-icon-right"></span>
            <span class="zm-date-range-picker__editors-wrap is-right">
              <span class="zm-date-range-picker__time-picker-wrap">
                <zm-input
                  size="small"
                  :disabled="rangeState.selecting"
                  :placeholder="t('datepicker.endDate')"
                  class="zm-date-range-picker__editor"
                  :value="maxVisibleDate"
                  :readonly="!minDate"
                  @input.native="handleDateInput($event, 'max')"
                  @change.native="handleDateChange($event, 'max')"/>
              </span>
              <span class="zm-date-range-picker__time-picker-wrap" v-clickoutside="handleMaxTimeClose">
                <zm-input
                  size="small"
                  :disabled="rangeState.selecting"
                  ref="maxInput"
                  :placeholder="t('datepicker.endTime')"
                  class="zm-date-range-picker__editor"
                  :value="maxVisibleTime"
                  @focus="minDate && (maxTimePickerVisible = true)"
                  :readonly="!minDate"
                  @change.native="handleTimeChange($event, 'max')"/>
                <time-picker
                  ref="maxTimePicker"
                  @pick="handleMaxTimePick"
                  :time-arrow-control="arrowControl"
                  :visible="maxTimePickerVisible"
                  @mounted="$refs.maxTimePicker.format=timeFormat">
                </time-picker>
              </span>
            </span>
          </div>
          <div class="zm-picker-panel__content zm-date-range-picker__content is-left">
            <div class="zm-date-range-picker__header">
              <button
                type="button"
                @click="leftPrevYear"
                :aria-label="getPrevYearAria()"
                class="zm-picker-panel__icon-btn zm-date-picker__prev-btn zm-icon-left-1"></button>
              <button
                type="button"
                @click="leftPrevMonth"
                :aria-label="getPrevMonthAria()"
                class="zm-picker-panel__icon-btn zm-date-picker__prev-btn zm-icon-left"></button>
              <div>{{ leftLabel }}</div>
              <button
                type="button"
                @click="leftNextMonth"
                v-if="unlinkPanels"
                :disabled="!enableMonthArrow"
                :class="{ 'is-disabled': !enableMonthArrow }"
                class="zm-picker-panel__icon-btn zm-date-picker__next-btn zm-icon-right"></button>
              <button
                type="button"
                @click="leftNextYear"
                v-if="unlinkPanels"
                :disabled="!enableYearArrow"
                :class="{ 'is-disabled': !enableYearArrow }"
                class="zm-picker-panel__icon-btn zm-date-picker__next-btn  zm-icon-right-1"></button>
            </div>
            <date-table
              selection-mode="range"
              pos="left"
              key="left"
              ref="leftDateTable"
              :date="leftDate"
              :value="leftDateValue"
              :default-value="defaultValue"
              :min-date="minDate"
              :max-date="maxDate"
              :range-state="rangeState"
              :current-focus-date="currentFocusDate"
              :disabled-date="disabledDate"
              @changerange="handleChangeRange"
              :first-day-of-week="firstDayOfWeek"
              @pick="handleRangePick"
              @focus="handleDateFocus"
              @mark-range="handleRangeMark"
              :current-date="currentDate"
            >
            </date-table>
          </div>
          <div class="zm-picker-panel__content zm-date-range-picker__content is-right">
            <div class="zm-date-range-picker__header">
              <button
                type="button"
                @click="rightPrevYear"
                v-if="unlinkPanels"
                :disabled="!enableYearArrow"
                :class="{ 'is-disabled': !enableYearArrow }"
                class="zm-picker-panel__icon-btn zm-date-picker__prev-btn zm-icon-left-1"></button>
              <button
                type="button"
                @click="rightPrevMonth"
                v-if="unlinkPanels"
                :disabled="!enableMonthArrow"
                :class="{ 'is-disabled': !enableMonthArrow }"
                class="zm-picker-panel__icon-btn zm-date-picker__prev-btn zm-icon-left"></button>
              <div>{{ rightLabel }}</div>
              <button
                type="button"
                @click="rightNextMonth"
                class="zm-picker-panel__icon-btn zm-date-picker__next-btn zm-icon-right"
                :aria-label="getNextMonthAria()"
              ></button>
              <button
                type="button"
                @click="rightNextYear"
                class="zm-picker-panel__icon-btn zm-date-picker__next-btn  zm-icon-right-1"
                :aria-label="getNextYearAria()"
              ></button>
            </div>
            <date-table
              selection-mode="range"
              pos="right"
              key="right"
              ref="rightDateTable"
              :date="rightDate"
              :value="rightDateValue"
              :default-value="defaultValue"
              :min-date="minDate"
              :max-date="maxDate"
              :current-focus-date="currentFocusDate"
              :range-state="rangeState"
              :disabled-date="disabledDate"
              @changerange="handleChangeRange"
              :first-day-of-week="firstDayOfWeek"
              @pick="handleRangePick"
              @focus="handleDateFocus"
              @mark-range="handleRangeMark"
              :current-date="currentDate"
            >
            </date-table>
          </div>
        </div>
      </div>
      <div class="zm-picker-panel__footer" v-if="showTime">
        <zm-button
          size="small"
          type="text"
          class="zm-picker-panel__link-btn"
          @click="handleClear">
          {{ t('datepicker.clear') }}
        </zm-button>
        <zm-button
          plain
          size="small"
          class="zm-picker-panel__link-btn"
          :disabled="btnDisabled"
          @click="handleConfirm(false)">
          {{ t('datepicker.confirm') }}
        </zm-button>
      </div>
    </div>
  </transition>
</template>

<script type="text/babel">
  import { shouldIgnoreUnit } from '../../../locale';
  import {
    formatDate,
    parseDate,
    isDate,
    modifyDate,
    modifyTime,
    modifyWithTimeString,
    prevYear,
    nextYear,
    prevMonth,
    nextMonth,
    extractDateFormat,
    extractTimeFormat,
    getDateI18n
  } from '../util';
  import Clickoutside from '../../../utils/clickoutside';
  import Locale from '../../../mixins/locale';
  import TimePicker from './time';
  import DateTable from '../basic/date-table';
  import ZmInput from '../../input';
  import ZmButton from '../../button';
  import AriaDialog from '../../../utils/aria-dialog';
  import { readAria } from '../../../utils/aria';
  import AriaUtils from '../../../utils/aria-utils';

  const advanceDate = (date, amount) => {
    return new Date(new Date(date).getTime() + amount);
  };

  const calcDefaultValue = (defaultValue) => {
    if (Array.isArray(defaultValue)) {
      return [new Date(defaultValue[0]), new Date(defaultValue[1])];
    } else if (defaultValue) {
      return [new Date(defaultValue), advanceDate(defaultValue, 24 * 60 * 60 * 1000)];
    } else {
      return [new Date(), advanceDate(Date.now(), 24 * 60 * 60 * 1000)];
    }
  };

  export default {
    mixins: [Locale],

    directives: { Clickoutside },

    computed: {
      btnDisabled() {
        return !(this.minDate && this.maxDate && !this.selecting && this.isValidValue([this.minDate, this.maxDate]));
      },

      leftLabel() {
        const yearTrans = shouldIgnoreUnit() ? this.t('datepicker.year') : '';
        return this.leftDate.getFullYear() + ' ' + yearTrans + ' ' + this.t(`datepicker.month${ this.leftDate.getMonth() + 1 }`);
      },

      rightLabel() {
        const yearTrans = shouldIgnoreUnit() ? this.t('datepicker.year') : '';
        return this.rightDate.getFullYear() + ' ' + yearTrans + ' ' + this.t(`datepicker.month${ this.rightDate.getMonth() + 1 }`);
      },

      leftYear() {
        return this.leftDate.getFullYear();
      },

      leftMonth() {
        return this.leftDate.getMonth();
      },

      leftMonthDate() {
        return this.leftDate.getDate();
      },

      rightYear() {
        return this.rightDate.getFullYear();
      },

      rightMonth() {
        return this.rightDate.getMonth();
      },

      rightMonthDate() {
        return this.rightDate.getDate();
      },

      minVisibleDate() {
        return this.minDate ? formatDate(this.minDate, this.dateFormat) : '';
      },

      maxVisibleDate() {
        return (this.maxDate || this.minDate) ? formatDate(this.maxDate || this.minDate, this.dateFormat) : '';
      },

      minVisibleTime() {
        return this.minDate ? formatDate(this.minDate, this.timeFormat) : '';
      },

      maxVisibleTime() {
        return (this.maxDate || this.minDate) ? formatDate(this.maxDate || this.minDate, this.timeFormat) : '';
      },

      timeFormat() {
        if (this.format) {
          return extractTimeFormat(this.format);
        } else {
          return 'HH:mm:ss';
        }
      },

      dateFormat() {
        if (this.format) {
          return extractDateFormat(this.format);
        } else {
          return 'MM-dd-yyyy';
        }
      },

      enableMonthArrow() {
        const nextMonth = (this.leftMonth + 1) % 12;
        const yearOffset = this.leftMonth + 1 >= 12 ? 1 : 0;
        return this.unlinkPanels && new Date(this.leftYear + yearOffset, nextMonth) < new Date(this.rightYear, this.rightMonth);
      },

      enableYearArrow() {
        return this.unlinkPanels && this.rightYear * 12 + this.rightMonth - (this.leftYear * 12 + this.leftMonth + 1) >= 12;
      }
    },

    data() {
      return {
        popperClass: '',
        value: [],
        defaultValue: null,
        defaultTime: null,
        minDate: '',
        maxDate: '',
        leftDate: new Date(),
        rightDate: nextMonth(new Date()),
        rangeState: {
          endDate: null,
          selecting: false,
          row: null,
          column: null
        },
        showTime: false,
        shortcuts: '',
        visible: '',
        disabledDate: '',
        firstDayOfWeek: 7,
        minTimePickerVisible: false,
        maxTimePickerVisible: false,
        format: '',
        arrowControl: true,
        unlinkPanels: false,
        currentFocusDate: null,
        leftDateValue: null,
        rightDateValue: null,
        markedValue: null,
        entering: false,
        enterDate: null,
        currentDate: null
      };
    },

    watch: {
      minDate(val) {
        this.$nextTick(() => {
          if (this.$refs.maxTimePicker && this.maxDate && this.maxDate < this.minDate) {
            const format = 'HH:mm:ss';
            this.$refs.maxTimePicker.selectableRange = [
              [
                parseDate(formatDate(this.minDate, format), format),
                parseDate('23:59:59', format)
              ]
            ];
          }
        });
        if (val && this.$refs.minTimePicker) {
          this.$refs.minTimePicker.date = val;
          this.$refs.minTimePicker.value = val;
        }
      },

      maxDate(val) {
        if (val && this.$refs.maxTimePicker) {
          this.$refs.maxTimePicker.date = val;
          this.$refs.maxTimePicker.value = val;
        }
      },

      minTimePickerVisible(val) {
        if (val) {
          this.$nextTick(() => {
            this.$refs.minTimePicker.date = this.minDate;
            this.$refs.minTimePicker.value = this.minDate;
            this.$refs.minTimePicker.adjustSpinners();
          });
        }
      },

      maxTimePickerVisible(val) {
        if (val) {
          this.$nextTick(() => {
            this.$refs.maxTimePicker.date = this.maxDate;
            this.$refs.maxTimePicker.value = this.maxDate;
            this.$refs.maxTimePicker.adjustSpinners();
          });
        }
      },

      value(newVal) {
        if (!newVal) {
          this.minDate = null;
          this.maxDate = null;
        } else if (Array.isArray(newVal)) {
          this.minDate = isDate(newVal[0]) ? new Date(newVal[0]) : null;
          this.maxDate = isDate(newVal[1]) ? new Date(newVal[1]) : null;
          if (this.minDate) {
            this.leftDate = this.minDate;
            if (this.unlinkPanels && this.maxDate) {
              const minDateYear = this.minDate.getFullYear();
              const minDateMonth = this.minDate.getMonth();
              const maxDateYear = this.maxDate.getFullYear();
              const maxDateMonth = this.maxDate.getMonth();
              this.rightDate = minDateYear === maxDateYear && minDateMonth === maxDateMonth
                ? nextMonth(this.maxDate)
                : this.maxDate;
            } else {
              this.rightDate = nextMonth(this.leftDate);
            }
          } else {
            this.leftDate = calcDefaultValue(this.defaultValue)[0];
            this.rightDate = nextMonth(this.leftDate);
          }
        }
      },

      defaultValue(val) {
        if (!Array.isArray(this.value)) {
          const [left, right] = calcDefaultValue(val);
          this.leftDate = left;
          this.rightDate = val && val[1] && this.unlinkPanels
            ? right
            : nextMonth(this.leftDate);
        }
      },

      leftDate() {
        this.ariaDialog && this.ariaDialog.refresh();
      }
    },

    methods: {
      handleClear() {
        this.minDate = null;
        this.maxDate = null;
        this.leftDate = calcDefaultValue(this.defaultValue)[0];
        this.rightDate = nextMonth(this.leftDate);
        this.$emit('pick', null);
      },

      handleChangeRange(val) {
        this.minDate = val.minDate;
        this.maxDate = val.maxDate;
        this.rangeState = val.rangeState;
      },

      handleDateInput(event, type) {
        const value = event.target.value;
        if (value.length !== this.dateFormat.length) return;
        const parsedValue = parseDate(value, this.dateFormat);

        if (parsedValue) {
          if (typeof this.disabledDate === 'function' &&
            this.disabledDate(new Date(parsedValue))) {
            return;
          }
          if (type === 'min') {
            this.minDate = new Date(parsedValue);
            this.leftDate = new Date(parsedValue);
            this.rightDate = nextMonth(this.leftDate);
          } else {
            this.maxDate = new Date(parsedValue);
            this.leftDate = prevMonth(parsedValue);
            this.rightDate = new Date(parsedValue);
          }
        }
      },

      handleDateChange(event, type) {
        const value = event.target.value;
        const parsedValue = parseDate(value, this.dateFormat);
        if (parsedValue) {
          if (type === 'min') {
            this.minDate = modifyDate(this.minDate, parsedValue.getFullYear(), parsedValue.getMonth(), parsedValue.getDate());
            if (this.minDate > this.maxDate) {
              this.maxDate = this.minDate;
            }
          } else {
            this.maxDate = modifyDate(this.maxDate, parsedValue.getFullYear(), parsedValue.getMonth(), parsedValue.getDate());
            if (this.maxDate < this.minDate) {
              this.minDate = this.maxDate;
            }
          }
        }
      },

      handleTimeChange(event, type) {
        const value = event.target.value;
        const parsedValue = parseDate(value, this.timeFormat);
        if (parsedValue) {
          if (type === 'min') {
            this.minDate = modifyTime(this.minDate, parsedValue.getHours(), parsedValue.getMinutes(), parsedValue.getSeconds());
            if (this.minDate > this.maxDate) {
              this.maxDate = this.minDate;
            }
            this.$refs.minTimePicker.value = this.minDate;
            this.minTimePickerVisible = false;
          } else {
            this.maxDate = modifyTime(this.maxDate, parsedValue.getHours(), parsedValue.getMinutes(), parsedValue.getSeconds());
            if (this.maxDate < this.minDate) {
              this.minDate = this.maxDate;
            }
            this.$refs.maxTimePicker.value = this.minDate;
            this.maxTimePickerVisible = false;
          }
        }
      },

      handleRangePick(val, close = true) {
        const defaultTime = this.defaultTime || [];
        const minDate = modifyWithTimeString(val.minDate, defaultTime[0]);
        const maxDate = modifyWithTimeString(val.maxDate, defaultTime[1]);
        const newDate = modifyWithTimeString(val.newDate);

        if (this.maxDate === maxDate && this.minDate === minDate) {
          return;
        }
        this.onPick && this.onPick(val);
        this.maxDate = maxDate;
        this.minDate = minDate;

        // workaround for https://github.com/ElemeFE/element/issues/7539, should remove this block when we don't have to care about Chromium 55 - 57
        setTimeout(() => {
          this.maxDate = maxDate;
          this.minDate = minDate;
        }, 10);

        this.currentFocusDate = newDate;
        this.updateDate(newDate);

        if (!close || this.showTime) return;
        this.handleConfirm();
      },

      handleDateFocus(value) {
        this.currentFocusDate = value;
        this.updateDate(this.currentFocusDate);
      },

      handleRangeMark(markedValue) {
        if (!this.markedValue) {
          this.markedValue = markedValue;
        }
        if (markedValue && this.markedValue && markedValue[0] && markedValue[1] && (markedValue[0] !== this.markedValue[0] || markedValue[1] !== this.markedValue[1])) {
          this.markedValue = markedValue;
          readAria(`Select Date from ${this.getAriaLabel(markedValue[0])} to ${this.getAriaLabel(markedValue[1])}`);
        }
      },

      getAriaLabel(date) {
        const WEEKS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
        return getDateI18n(date) + ' ' + this.t('datepicker.weeks.' + WEEKS[date.getDay()]);
      },

      handleShortcutClick(shortcut) {
        if (shortcut.onClick) {
          shortcut.onClick(this);
        }
      },

      handleMinTimePick(value, visible, first) {
        this.minDate = this.minDate || new Date();
        if (value) {
          this.minDate = modifyTime(this.minDate, value.getHours(), value.getMinutes(), value.getSeconds());
        }

        if (!first) {
          this.minTimePickerVisible = visible;
        }

        if (!this.maxDate || this.maxDate && this.maxDate.getTime() < this.minDate.getTime()) {
          this.maxDate = new Date(this.minDate);
        }
      },

      handleMinTimeClose() {
        this.minTimePickerVisible = false;
      },

      handleMaxTimePick(value, visible, first) {
        if (this.maxDate && value) {
          this.maxDate = modifyTime(this.maxDate, value.getHours(), value.getMinutes(), value.getSeconds());
        }

        if (!first) {
          this.maxTimePickerVisible = visible;
        }

        if (this.maxDate && this.minDate && this.minDate.getTime() > this.maxDate.getTime()) {
          this.minDate = new Date(this.maxDate);
        }
      },

      handleMaxTimeClose() {
        this.maxTimePickerVisible = false;
      },

      // leftPrev*, rightNext* need to take care of `unlinkPanels`
      leftPrevYear() {
        this.leftDate = prevYear(this.leftDate);
        if (!this.unlinkPanels) {
          this.rightDate = nextMonth(this.leftDate);
        }
      },

      getPrevYearAria() {
        // const date = prevYear(this.leftDate);
        // return 'Previous Year,' + getDateI18n(date, true);
        return 'Previous Year';
      },

      leftPrevMonth() {
        this.leftDate = prevMonth(this.leftDate);
        if (!this.unlinkPanels) {
          this.rightDate = nextMonth(this.leftDate);
        }
      },

      getPrevMonthAria() {
        // const date = prevMonth(this.leftDate);
        // return 'Previous Month,' + getDateI18n(date, true);
        return 'Previous Month';
      },

      rightNextYear() {
        if (!this.unlinkPanels) {
          this.leftDate = nextYear(this.leftDate);
          this.rightDate = nextMonth(this.leftDate);
        } else {
          this.rightDate = nextYear(this.rightDate);
        }
      },

      getNextYearAria() {
        // const date = nextYear(this.rightDate);
        // return 'Next Year,' + getDateI18n(date, true);
        return 'Next Year';
      },

      rightNextMonth() {
        if (!this.unlinkPanels) {
          this.leftDate = nextMonth(this.leftDate);
          this.rightDate = nextMonth(this.leftDate);
        } else {
          this.rightDate = nextMonth(this.rightDate);
        }
      },

      getNextMonthAria() {
        // const date = nextMonth(this.rightDate);
        // return 'Next Month,' + getDateI18n(date, true);
        return 'Next Month';
      },

      // leftNext*, rightPrev* are called when `unlinkPanels` is true
      leftNextYear() {
        this.leftDate = nextYear(this.leftDate);
      },

      leftNextMonth() {
        this.leftDate = nextMonth(this.leftDate);
      },

      rightPrevYear() {
        this.rightDate = prevYear(this.rightDate);
      },

      rightPrevMonth() {
        this.rightDate = prevMonth(this.rightDate);
      },

      handleConfirm(visible = false) {
        if (!visible) {
          this.entering = false;
        }
        if (this.isValidValue([this.minDate, this.maxDate])) {
          this.$emit('pick', [this.minDate, this.maxDate], visible);
        }
      },
      handleEnter() {
        document.body.addEventListener('keydown', this.handleKeydown);
        const focusAfterClosed = document.activeElement;
        this.$nextTick(() => {
          if (!this.ariaDialog) {
            let firstFocusEl = this.$el.querySelector('.current a');
            if (!firstFocusEl) {
              firstFocusEl = this.$el.querySelector('.start-date a');
              if (firstFocusEl) {
                this.currentFocusDate = this.minDate;
              } else {
                firstFocusEl = this.$el.querySelector('.today a');
              }
            }
            this.ariaDialog = new AriaDialog(this.$el, focusAfterClosed, firstFocusEl);
          } else {
            this.ariaDialog.setFocusEl();
          }
        });
      },
      handleLeave() {
        this.$emit('dodestroy');
        this.flagLeave = true;
        this.ariaDialog && this.ariaDialog.closeDialog();
        this.ariaDialog = null;
        document.body.removeEventListener('keydown', this.handleKeydown);
      },
      handleKeydown(event) {
        const keyCode = event.keyCode;
        const list = [38, 40, 37, 39];
        if (this.visible) {
          const inTable = document.activeElement.tagName === 'A';
          if (list.indexOf(keyCode) !== -1) {
            if (inTable) {
              this.handleKeyControl(keyCode);
            }
            event.stopPropagation();
            event.preventDefault();
          } else if (keyCode === AriaUtils.keys.enter || keyCode === AriaUtils.keys.space) { // enter or space
            if (inTable) {
              if (!this.entering) {
                this.entering = true;
                this.enterDate = this.currentFocusDate;
                this.minDate = this.maxDate = this.enterDate;
              } else {
                if (!this.maxDate) {
                  this.maxDate = this.minDate;
                }
                this.handleConfirm();
              }
              event.stopPropagation();
              event.preventDefault();
            }
          } else if (keyCode === AriaUtils.keys.esc) {
            if (inTable) {
              if (this.entering) {
                this.entering = false;
                this.minDate = null;
                this.maxDate = null;
                return;
              }
            }
            this.$emit('escClose');
            this.visible = false;
            event.preventDefault();
            event.stopPropagation();
          }
        }
      },
      handleKeyControl(keyCode) {
        const mapping = {
          'year': {
            38: -4, 40: 4, 37: -1, 39: 1, offset: (date, step) => date.setFullYear(date.getFullYear() + step)
          },
          'month': {
            38: -4, 40: 4, 37: -1, 39: 1, offset: (date, step) => date.setMonth(date.getMonth() + step)
          },
          'week': {
            38: -1, 40: 1, 37: -1, 39: 1, offset: (date, step) => date.setDate(date.getDate() + step * 7)
          },
          'day': {
            38: -7, 40: 7, 37: -1, 39: 1, offset: (date, step) => date.setDate(date.getDate() + step)
          }
        };
        const mode = 'day';
        const year = 3.1536e10;
        const currentFocusDate = this.currentFocusDate || new Date();
        const now = currentFocusDate.getTime();
        const newDate = new Date(currentFocusDate.getTime());
        const disabledDate = this.disabledDate;
        while (Math.abs(now - newDate.getTime()) <= year) {
          const map = mapping[mode];
          map.offset(newDate, map[keyCode]);
          if (typeof disabledDate === 'function' && disabledDate(newDate)) {
            continue;
          }
          this.currentFocusDate = newDate;
          this.updateDate(newDate);
          if (this.entering) {
            const now = newDate.getTime();
            const enterTime = this.enterDate.getTime();
            if (now < enterTime) {
              this.minDate = new Date(now);
              this.maxDate = new Date(enterTime);
            } else {
              this.minDate = new Date(enterTime);
              this.maxDate = new Date(now);
            }
          }
          break;
        }
      },
      updateDate(date) {
        const year = this.$refs.rightDateTable.year;
        const month = this.$refs.rightDateTable.month;
        const startDate = new Date(year, month);
        let flag = 'left';
        if (date.getTime() < startDate.getTime()) {
          this.leftDate = date;
          this.leftDateValue = date;
          if (!this.unlinkPanels) {
            this.rightDate = nextMonth(this.leftDate);
          }
          this.rightDateValue = null;
        } else {
          this.rightDate = date;
          this.rightDateValue = date;
          if (!this.unlinkPanels) {
            this.leftDate = prevMonth(this.rightDate);
          }
          this.leftDateValue = null;
          flag = 'right';
        }
        this.setFocusDay(flag);
      },
      setFocusDay(flag) {
        let $el = this.$refs.leftDateTable.$el;
        if (flag === 'right') {
          $el = this.$refs.rightDateTable.$el;
        }
        this.$nextTick(() => {
          if ($el.querySelector('.current a')) {
            $el.querySelector('.current a').focus();
          } else {
            $el.querySelector('.today a') && $el.querySelector('.today a').focus();
          }
        });
      },
      isValidValue(value) {
        return Array.isArray(value) &&
          value && value[0] && value[1] &&
          isDate(value[0]) && isDate(value[1]) &&
          value[0].getTime() <= value[1].getTime() && (
          typeof this.disabledDate === 'function'
            ? !this.disabledDate(value[0]) && !this.disabledDate(value[1])
            : true
        );
      },

      resetView() {
        // NOTE: this is a hack to reset {min, max}Date on picker open.
        // TODO: correct way of doing so is to refactor {min, max}Date to be dependent on value and internal selection state
        //       an alternative would be resetView whenever picker becomes visible, should also investigate date-panel's resetView
        this.minDate = this.value && isDate(this.value[0]) ? new Date(this.value[0]) : null;
        this.maxDate = this.value && isDate(this.value[0]) ? new Date(this.value[1]) : null;
      }
    },

    components: { TimePicker, DateTable, ZmInput, ZmButton }
  };
</script>

<style lang="scss">
  @import "../../../scss/common/var";
  @import "../../../scss/mixins/mixins";

  @include b(date-range-picker) {
    width: 646px;

    &.has-sidebar {
      width: 756px;
    }

    table {
      table-layout: fixed;
      width: 100%;
    }

    .zm-picker-panel__body {
      min-width: 513px;
    }

    .zm-picker-panel__content {
      margin: 0;
    }

    @include e(header) {
      position: relative;
      /*text-align: center;*/
      display: flex;
      height: 28px;

      div {
        font-size: 16px;
        font-weight: 500;
        text-align: center;
        flex: 1;
      }
    }

    @include e(content) {
      float: left;
      width: 50%;
      box-sizing: border-box;
      margin: 0;
      padding: 16px;

      @include when(left) {
        border-right: 1px solid $--datepicker-inner-border-color;
      }

      @include when(right) {
        .zm-date-range-picker__header {

          div {
            /*margin-left: 50px;
            margin-right: 50px;*/
          }
        }
      }
    }

    @include e(editors-wrap) {
      box-sizing: border-box;
      display: table-cell;

      @include when(right) {
        text-align: right;
      }
    }

    @include e(time-header) {
      position: relative;
      border-bottom: 1px solid $--datepicker-inner-border-color;
      font-size: 12px;
      padding: 8px 5px 5px 5px;
      display: table;
      width: 100%;
      box-sizing: border-box;

      > .zm-icon-right {
        font-size: 20px;
        vertical-align: middle;
        display: table-cell;
        color: $--datepicker-icon-color;
      }
    }

    @include e(time-picker-wrap) {
      position: relative;
      display: table-cell;
      padding: 0 5px;

      .zm-picker-panel {
        position: absolute;
        top: 13px;
        right: 0;
        z-index: 1;
        background: $--color-white;
      }
    }
  }
</style>
